import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { Layout } from "../../layouts/Layout";
import { CHECK_ICON } from "../../lib/assets";
import { ServicesDetailsWrapper } from "../../components/ServicesDetails/ServicesDetailsWrapper";
import { BrandSeven } from "../../components/Brand/BrandSeven";
import { ServicesDetailsFaq } from "../../components/ServicesDetails/ServicesDetailsFaq";
import { COMPANY, SERVICES } from "../../content/siteContent";
import { getServiceSeo } from "../../components/SEO/Seo";
import { ContactNumbers } from "../../components/ContactNumbers/ContactNumbers";

const serviceHeroImages = {
  "secp-services": {
    fileName: "secp.jpeg",
    alt: "SECP company registration services poster",
  },
  "boi-registration": {
    fileName: "boi.jpeg",
    alt: "BOI registration poster",
  },
  "pec-services": {
    fileName: "pec.jpeg",
    alt: "PEC registration poster",
  },
  "additional-services": {
    fileName: "goverment.jpeg",
    alt: "Additional professional services poster",
  },
  "aedb-services": {
    fileName: "aedb.jpeg",
    alt: "AEDB and PPIB registration poster",
  },
  "fbr-services": {
    fileName: "tax_Registration.jpeg",
    alt: "Tax registration services poster",
  },
  "audit-report": {
    fileName: "audit.jpeg",
    alt: "Audit services poster",
  },
  "chamber-of-commerce": {
    fileName: "chamber.jpeg",
    alt: "Chamber membership services poster",
  },
  "import-export-registration": {
    fileName: "import_export.jpeg",
    alt: "Import and export services poster",
  },
  "income-tax-return": {
    fileName: "income_tax_return.jpeg",
    alt: "Income tax return services poster",
  },
  "professional-tax-certificate": {
    fileName: "tax_certification.jpeg",
    alt: "Professional tax certification poster",
  },
  "pra-bra-srb-registration": {
    fileName: "pra.jpeg",
    alt: "PRA KPRA BRA and SRB registration services poster",
  },
};

const ServicePoster = ({ image }) => {
  if (!image) {
    return null;
  }

  const posterSrc = `${process.env.PUBLIC_URL}/images/services-details/${image.fileName}`;

  return (
    <div className="services-details-thumb-two service-detail-poster">
      <img
        src={posterSrc}
        alt={image.alt}
        loading="lazy"
        onError={(event) => {
          event.currentTarget.closest(".service-detail-poster")?.remove();
        }}
      />
    </div>
  );
};

const ServicesDetailsPageFive = () => {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const service = SERVICES.find((item) => item.slug === slug) || SERVICES[0];
  const serviceSeo = getServiceSeo(service.slug);
  const serviceHeroImage = serviceHeroImages[service.slug];

  return (
    <Layout
      breadcrumb={"Services"}
      title={service.pageTitle}
      seo={{
        title: serviceSeo?.title || service.pageTitle,
        description: serviceSeo?.description || service.intro,
        pathname,
        schemaType: "service",
      }}
    >
      <ServicesDetailsWrapper>
        <div className="services-details-wrap service-detail-readable">
          <div className="services-details-content">
            <h2 className="title">{service.shortTitle}</h2>
            <p className="service-detail-intro">{service.intro}</p>

            <ServicePoster image={serviceHeroImage} />

            {service.overviewItems ? (
              <div className="speed-optimized-wrap">
                <h4 className="title-two">{service.overviewTitle}</h4>
                <div className="row">
                  {service.overviewItems.map(([title, text]) => (
                    <div key={title} className="col-lg-4 col-md-6">
                      <div className="services-item-four mb-4 h-100">
                        <div className="services-icon-four">
                          <i className="flaticon-check-mark"></i>
                        </div>
                        <div className="services-content-four">
                          <h2 className="title">{title}</h2>
                          <p>{text}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {service.contentSections ? (
              <div className="speed-optimized-wrap">
                {service.contentSections.map(([title, text]) => (
                  <div key={title} className="mb-4 service-detail-text-block">
                    <h4 className="title-two">{title}</h4>
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="speed-optimized-wrap">
              <h4 className="title-two">{service.offersTitle}</h4>
              {service.offers.map(([title, text], index) => (
                <div key={title} className="mb-4 service-detail-numbered-block">
                  <h5>
                    <span>{index + 1}.</span> {title}
                  </h5>
                  <p>{text}</p>
                </div>
              ))}
            </div>

            <div className="sd-inner-wrap-five">
              <div className="row">
                <div className="col-50">
                  <div className="left-side-content">
                    <h4 className="title-two">{service.highlightsTitle}</h4>
                    <p>
                      We tailor each engagement around the specific regulatory,
                      documentation, and compliance needs of your business.
                    </p>
                  </div>
                </div>
                <div className="col-50">
                  <div className="right-side-content">
                    <h4 className="title-two">How Zawix Supports You</h4>
                    <p>End-to-end guidance from consultation to submission.</p>
                    <ul className="list-wrap">
                      {(service.highlights || []).map((item) => (
                        <li key={item}>
                          <img src={CHECK_ICON} alt="" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {service.note ? (
              <div className="company-benefit-wrap service-detail-text-block">
                <h4 className="title-two">{service.noteTitle}</h4>
                <p>{service.note}</p>
              </div>
            ) : null}

            {service.feeTable ? (
              <div className="company-benefit-wrap">
                <h4 className="title-two">{service.feeTableTitle}</h4>
                <div className="table-responsive service-detail-table-wrap">
                  <table className="table service-detail-table">
                    <thead>
                      <tr>
                        {service.feeTable.columns.map((column) => (
                          <th key={column}>{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {service.feeTable.rows.map((row) => (
                        <tr key={row.join("-")}>
                          {row.map((cell) => (
                            <td key={cell}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {service.feeTableNote ? (
                  <p className="service-detail-muted">{service.feeTableNote}</p>
                ) : null}
              </div>
            ) : null}

            {service.extraChecklist ? (
              <div className="company-benefit-wrap">
                <h4 className="title-two">{service.extraChecklistTitle}</h4>
                <ul className="list-wrap">
                  {service.extraChecklist.map((item) => (
                    <li key={item}>
                      <img src={CHECK_ICON} alt="" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {service.scopeText ? (
              <div className="company-benefit-wrap service-detail-text-block">
                <h4 className="title-two">{service.scopeTitle}</h4>
                <p>{service.scopeText}</p>
              </div>
            ) : null}

            {service.caseStudies ? (
              <div className="company-benefit-wrap">
                <h4 className="title-two">{service.caseStudiesTitle}</h4>
                {service.caseStudies.map(([title, text], index) => (
                  <div key={title} className="mb-4 service-detail-numbered-block">
                    <h5>
                      <span>{index + 1}.</span> {title}
                    </h5>
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {service.processSteps ? (
              <div className="company-benefit-wrap">
                {service.processDuration ? (
                  <p>
                    <strong>{service.processDuration}</strong>
                  </p>
                ) : null}
                <h4 className="title-two">{service.processTitle}</h4>
                <p>{service.processSubtitle}</p>
                {service.processSteps.map(([title, text], index) => (
                  <div key={title} className="mb-4 service-detail-numbered-block">
                    <h5>
                      <span>{index + 1}.</span> {title}
                    </h5>
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {service.documentGroups ? (
              <div className="company-benefit-wrap">
                <h4 className="title-two">Required Documents & Information</h4>
                <div className="row">
                  {service.documentGroups.map(([title, items]) => (
                    <div key={title} className="col-lg-4 col-md-6">
                      <div className="right-side-content mb-4">
                        <h5>{title}</h5>
                        <ul className="list-wrap">
                          {items.map((item) => (
                            <li key={item}>
                              <img src={CHECK_ICON} alt="" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {service.faqs ? (
              <div className="company-benefit-wrap">
                <h4 className="title-two">{service.faqTitle}</h4>
                {service.faqs.map(([question, answer]) => (
                  <div key={question} className="mb-4 service-detail-faq-block">
                    <h5>{question}</h5>
                    <p>{answer}</p>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="company-benefit-wrap service-detail-text-block">
              <h4 className="title-two">Need tailored advice?</h4>
              <p>
                Call or WhatsApp us, or email {COMPANY.email} to discuss the
                right service for your business.
              </p>
              <ContactNumbers className="service-detail-contact-numbers" />
              <ServicesDetailsFaq />
            </div>
          </div>
        </div>
      </ServicesDetailsWrapper>

      <BrandSeven />
    </Layout>
  );
};

export default ServicesDetailsPageFive;
